import hokkaidoAirportCarApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_car';
import hokkaidoAirportReportApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_report';
import type {
  HokkaidoAirportReport,
  HokkaidoAirportState,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type { HokkaidoAirportCar } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportCar';

interface ReportCreationDependencies {
  getHanName: () => string | null | undefined;
  updateCarStatus: (status: string) => Promise<HokkaidoAirportCar | null>;
  saveLocalStorage: () => void;
}
interface UseHokkaidoAirportReportCreationResult {
  ensureReport: () => Promise<HokkaidoAirportReport | null>;
}

export const useHokkaidoAirportReportCreation = (
  state: HokkaidoAirportState,
  { getHanName, updateCarStatus, saveLocalStorage }: ReportCreationDependencies,
): UseHokkaidoAirportReportCreationResult => {
  let ensureReportPromise: Promise<HokkaidoAirportReport | null> | null = null;

  const ensureReport = async(): Promise<HokkaidoAirportReport | null> => {
    if (ensureReportPromise) return ensureReportPromise;

    ensureReportPromise = ensureReportOnce();
    try {
      return await ensureReportPromise;
    } finally {
      // 成功・失敗のどちらでも破棄し、次の押下で作り直せるようにする
      ensureReportPromise = null;
    }
  };
  // 未作成なら車両の登録と報告書の作成をし、車両に未紐付けなら紐付ける。班名・現在地が無く作れなければ null
  const ensureReportOnce = async(): Promise<HokkaidoAirportReport | null> => {
    // 班名が null のユーザーは作成 API が 422 になるため送らない
    const hanName = getHanName();
    if (!hanName || !state.baseDepartureTime) return null;

    // マウント時に現在地が取れなかった場合は車両IDが未採番のため、ここで登録する
    if (state.hokkaidoAirportCar.id === 0) {
      const car = await updateCarStatus(state.status);
      if (!car) return null;
    }

    if (state.hokkaidoAirportCar.report.id === 0) {
      // 業務開始時刻は、後から作る場合もマウント時に決めた時刻を使う
      const createdReport = await hokkaidoAirportReportApi.createHokkaidoAirportReport({
        car_id: state.hokkaidoAirportCar.id,
        base_departure_ts: state.baseDepartureTime,
        han_name: hanName,
      });
      // 紐付けが失敗して再操作したときに2件目を作らないよう、紐付けより先に画面の状態へ保存する
      state.hokkaidoAirportCar.report = createdReport;
      saveLocalStorage();
    }

    const report = state.hokkaidoAirportCar.report;
    if (state.hokkaidoAirportCar.currentReportId === report.id) return report;

    const car = await hokkaidoAirportCarApi.updateReportId(
      state.hokkaidoAirportCar.id,
      { current_report_id: report.id },
    );
    state.hokkaidoAirportCar.currentReportId = car.currentReportId;
    saveLocalStorage();
    return report;
  };

  return { ensureReport };
};
